/*
 * The Advanced Analytics age filter is a discrete slider: the handles snap to
 * the band edges below rather than moving freely between 0 and 100.
 *
 * vue-slider emits `change` before it emits `update:modelValue`, so a handler
 * that reads the bound value on `change` sees the previous position. The
 * filter (and the "Age: x - y" tag) then lags one move behind the handles - the
 * handles at 18 and 30 produced the tag "Age: 18 - 40". Convert the value the
 * slider emitted instead of reading state.
 *
 * Saved searches and deep links written for the old continuous slider can hold
 * any integer age. Those are widened outward to the surrounding band edges, so
 * the slider, the tag and the query all describe the same range and the results
 * never shrink.
 */

const AGE_BINS = [0, 18, 30, 40, 50, 65, 100]

const FIRST = AGE_BINS[0]
const LAST = AGE_BINS[AGE_BINS.length - 1]

const NO_FILTER = { startAge: '', endAge: '' }

// 0 is a real lower limit; only blanks mean "not set".
const hasAgeValue = (value) => value !== '' && value !== null && value !== undefined

const toNumber = (value) => (hasAgeValue(value) ? Number(value) : NaN)

const lowerEdge = (value) =>
  AGE_BINS.reduce((edge, bin) => (bin <= value ? bin : edge), FIRST)

const upperEdge = (value) =>
  AGE_BINS.find((bin) => bin >= value) ?? LAST

/**
 * The on-band filter values for a stored range.
 *
 * @param {number|string} startAge lower limit, '' when unset
 * @param {number|string} endAge upper limit, '' when unset
 * @returns {{startAge: number|string, endAge: number|string}} '' when there is
 *   no restriction (nothing set, or the full 0-100 span)
 */
const normalizeFilters = (startAge, endAge) => {
  const from = toNumber(startAge)
  const to = toNumber(endAge)
  if (!Number.isFinite(from) && !Number.isFinite(to)) return { ...NO_FILTER }
  const [low, high] = [
    Number.isFinite(from) ? from : FIRST,
    Number.isFinite(to) ? to : LAST
  ].sort((a, b) => a - b)
  const result = { startAge: lowerEdge(low), endAge: upperEdge(high) }
  if (result.startAge === FIRST && result.endAge === LAST) return { ...NO_FILTER }
  return result
}

/**
 * Turn the slider's emitted value into the demographics filter values.
 *
 * @param {Array<number>} value the [from, to] pair emitted by the slider
 * @returns {{startAge: number|string, endAge: number|string}} '' when unusable
 */
const filtersFromSlider = (value) => {
  if (!Array.isArray(value) || value.length !== 2) return { ...NO_FILTER }
  if (!Number.isFinite(toNumber(value[0])) || !Number.isFinite(toNumber(value[1]))) {
    return { ...NO_FILTER }
  }
  return normalizeFilters(value[0], value[1])
}

/**
 * Work out where the handles belong for the stored filter values.
 *
 * @param {number|string} startAge lower limit, '' when unset
 * @param {number|string} endAge upper limit, '' when unset
 * @returns {Array<number>} the [from, to] pair to bind to the slider
 */
const sliderFromFilters = (startAge, endAge) => {
  const { startAge: from, endAge: to } = normalizeFilters(startAge, endAge)
  return [hasAgeValue(from) ? from : FIRST, hasAgeValue(to) ? to : LAST]
}

/**
 * The pair of handles in ascending order, for drawing and for the v-model.
 * Dragging one handle past the other makes the slider emit the pair in dot
 * order, so the form must not keep that order.
 *
 * @param {Array<number>} value the [a, b] pair the slider holds
 * @returns {Array<number>|null} a new [low, high] pair, or null if malformed
 */
const sortedHandles = (value) => {
  if (!Array.isArray(value) || value.length !== 2) return null
  const [a, b] = value.map(toNumber)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return a <= b ? [a, b] : [b, a]
}

export default {
  AGE_BINS,
  filtersFromSlider,
  sliderFromFilters,
  normalizeFilters,
  hasAgeValue,
  sortedHandles
}
